import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { displayApi } from "@/lib/display";
import { money } from "@/lib/format";
import { PHONE_HINT, PROVIDERS, fmtPhone, ghPhone, guessProvider, providerLabel, type Intent, type Provider } from "@/lib/paystack";
import { mediaUrl } from "@/lib/media";
import { useTill } from "@/lib/till-context";
import { Brand } from "./Brand";
import { Qr } from "./Qr";
import { cx } from "./ui";

type CatItem = { id: string; name: string; brand: string; category: string; image: string | null; price: number; tracking: string; stock: "in" | "low" | "out"; left: number | null };
type Catalog = { mode: "sale" | "pick" | "pay"; selfPayScope: "all" | "accessories"; showStock: boolean; items: CatItem[]; categories: string[] };
type Order = { id: string; number: number; total: number; status: string; saleNumber: string | null };
type Screen = "browse" | "details" | "pay" | "number" | "otp" | "wait" | "qr" | "paid" | "sent";
type Details = { name: string; phone: string; email: string };

const IDLE_RESET = 3 * 60_000;
const digits = (v: string) => ghPhone(v).keep;

/**
 * The customer display when no sale is being rung up: customers browse, build their order, and
 * send it to the cashier or (when allowed) pay for it here with Paystack, then collect it at the
 * counter with their number. Self-checkout asks for a phone number (receipt and warranty go to it).
 */
export function DisplayShop() {
  const { store } = useTill();
  const [cat, setCat] = useState<Catalog | null>(null);
  const [category, setCategory] = useState("All");
  const [basket, setBasket] = useState<Record<string, number>>({});
  const [screen, setScreen] = useState<Screen>("browse");
  const [after, setAfter] = useState<"pay" | "send">("pay");
  const [details, setDetails] = useState<Details>({ name: "", phone: "", email: "" });
  const [order, setOrder] = useState<Order | null>(null);
  const [intent, setIntent] = useState<Intent | null>(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const touched = useRef(Date.now());

  const load = useCallback(() => {
    displayApi<Catalog>("/display/catalog").then(setCat).catch(() => setCat((c) => c ?? { mode: "sale", selfPayScope: "all", showStock: false, items: [], categories: [] }));
  }, []);
  useEffect(() => {
    load();
    const h = setInterval(load, 5 * 60_000);
    return () => clearInterval(h);
  }, [load]);

  const reset = useCallback(() => {
    setBasket({});
    setOrder(null);
    setIntent(null);
    setErr("");
    setDetails({ name: "", phone: "", email: "" });
    setScreen("browse");
    setCategory("All");
  }, []);
  useEffect(() => {
    const h = setInterval(() => {
      if (Date.now() - touched.current > IDLE_RESET && (Object.keys(basket).length || screen !== "browse") && screen !== "wait" && screen !== "qr") reset();
    }, 15_000);
    return () => clearInterval(h);
  }, [basket, screen, reset]);
  useEffect(() => {
    if (screen !== "paid" && screen !== "sent") return;
    const h = setTimeout(reset, 45_000);
    return () => clearTimeout(h);
  }, [screen, reset]);

  // While paying, ask the server (it checks Paystack and records the sale).
  useEffect(() => {
    if (!order || (screen !== "wait" && screen !== "qr")) return;
    const h = setInterval(async () => {
      try {
        const r = await displayApi<{ order: Order; intent: Intent | null }>(`/display/orders/${order.id}`);
        setOrder(r.order);
        if (r.intent) setIntent(r.intent);
        if (r.order.status === "paid" || r.order.status === "handed_over") setScreen("paid");
        else if (r.intent && (r.intent.status === "failed" || r.intent.status === "reversed")) {
          setErr(r.intent.message || "That payment didn't go through. Nothing was taken.");
          setScreen("pay");
        } else if (r.intent?.status === "send_otp") setScreen("otp");
      } catch {
        /* keep asking */
      }
    }, 4000);
    return () => clearInterval(h);
  }, [order, screen]);

  const items = cat?.items ?? [];
  const byId = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const lines = Object.entries(basket)
    .map(([id, qty]) => ({ item: byId.get(id), qty }))
    .filter((l): l is { item: CatItem; qty: number } => !!l.item);
  const total = lines.reduce((n, l) => n + l.item.price * l.qty, 0);
  const count = lines.reduce((n, l) => n + l.qty, 0);
  const hasDevice = lines.some((l) => l.item.tracking === "serial");
  const canPay = cat?.mode === "pay" && !(cat.selfPayScope === "accessories" && hasDevice);
  const setQty = (id: string, q: number) => {
    touched.current = Date.now();
    setBasket((b) => {
      const n = { ...b };
      if (q <= 0) delete n[id];
      else n[id] = Math.min(20, q);
      return n;
    });
    setOrder(null);
  };
  const customer = { name: details.name.trim(), phone: ghPhone(details.phone).local, email: details.email.trim() };

  const ensureOrder = async (send: boolean): Promise<Order | null> => {
    if (order && !send) return order;
    setBusy(true);
    setErr("");
    try {
      const r = await displayApi<{ order: Order }>("/display/orders", { lines: lines.map((l) => ({ itemId: l.item.id, qty: l.qty })), send, customer });
      setOrder(r.order);
      return r.order;
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't send your order");
      return null;
    } finally {
      setBusy(false);
    }
  };
  const sendToCashier = async () => {
    const o = await ensureOrder(true);
    if (o) setScreen("sent");
  };
  const pay = async (body: { method: "momo" | "card"; phone?: string; provider?: Provider }) => {
    const o = await ensureOrder(false);
    if (!o) return;
    setBusy(true);
    setErr("");
    try {
      const r = await displayApi<{ order: Order; intent: Intent }>(`/display/orders/${o.id}/pay`, { ...body, contactPhone: customer.phone, name: customer.name, email: customer.email });
      setOrder(r.order);
      setIntent(r.intent);
      setScreen(r.intent.channel === "qr" ? "qr" : r.intent.status === "send_otp" ? "otp" : "wait");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't start the payment");
    } finally {
      setBusy(false);
    }
  };
  const sendOtp = async (otp: string) => {
    if (!order) return;
    setBusy(true);
    try {
      const r = await displayApi<{ order: Order; intent: Intent | null }>(`/display/orders/${order.id}/otp`, { otp });
      setOrder(r.order);
      if (r.intent) setIntent(r.intent);
      if (r.order.status === "paid") setScreen("paid");
      else if (r.intent?.status === "failed") {
        setErr(r.intent.message || "That code didn't work. Try again.");
        setScreen("pay");
      } else if (r.intent?.status !== "send_otp") setScreen("wait");
      else setErr(r.intent.message || "That code didn't work. Check it and try again.");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "That code didn't work");
    } finally {
      setBusy(false);
    }
  };

  if (!cat || cat.mode === "sale")
    return (
      <div className="flex h-full flex-col items-center justify-center gap-5 bg-ink px-6 text-center text-white">
        <Brand big sub="Welcome" />
        <p className="text-lg text-white/65">{store?.storeName ? `Welcome to ${store.storeName}` : "Welcome"}</p>
      </div>
    );

  if (screen === "paid" || screen === "sent")
    return (
      <div className="flex h-full flex-col items-center justify-center gap-5 bg-ink px-6 text-center text-white" onPointerDown={() => (touched.current = Date.now())}>
        <span className="font-mono text-sm text-brand-light">{screen === "paid" ? `PAID ✓ · ${money(order?.total ?? total)}` : "SENT TO THE CASHIER"}</span>
        <h1 className="text-3xl font-black sm:text-4xl">{screen === "paid" ? `Thank you${customer.name ? `, ${customer.name.split(" ")[0]}` : ""}! Please collect at the counter` : "Your order is at the counter"}</h1>
        <div className="flex flex-col items-center gap-1 bg-brand px-10 py-4">
          <span className="text-base font-bold">Your number</span>
          <span className="font-mono text-7xl font-semibold leading-none">{order?.number ?? "-"}</span>
        </div>
        <p className="max-w-xl text-base leading-relaxed text-white/75">
          {screen === "paid"
            ? `Your receipt is printing at the counter${customer.phone ? ` and has been texted to ${fmtPhone(customer.phone)}` : ""}. Phones and laptops are handed over sealed: the cashier scans the box so your warranty is registered.`
            : "Tell the cashier your number. They'll ring it up and you can pay there, by cash too."}
        </p>
        <button type="button" onClick={reset} className="h-12 border border-steel-2 px-6 font-bold">
          Done
        </button>
      </div>
    );

  if (screen !== "browse")
    return (
      <div className="flex min-h-full flex-col bg-ink text-white lg:h-full lg:flex-row" onPointerDown={() => (touched.current = Date.now())}>
        <section className="flex flex-1 flex-col gap-3 p-5 sm:p-8">
          <Brand sub="Your order" />
          <div className="mt-3 flex-1 overflow-y-auto">
            {lines.map((l) => (
              <div key={l.item.id} className="flex items-center gap-3 border-b border-steel-2 py-2.5">
                <Pic item={l.item} className="h-12 w-12" />
                <span className="min-w-0 flex-1 truncate text-[15px]">{l.qty} × {l.item.name}</span>
                <span className="font-mono text-[15px]">{money(l.item.price * l.qty)}</span>
              </div>
            ))}
          </div>
          <div className="flex items-baseline justify-between bg-brand px-5 py-4">
            <span className="font-bold">To pay</span>
            <span className="font-mono text-4xl font-semibold">{money(total)}</span>
          </div>
        </section>
        <aside className="flex w-full flex-col gap-3.5 bg-graphite p-5 sm:p-8 lg:w-[460px]">
          {err ? <p role="alert" className="bg-warn-tint px-3 py-2.5 text-sm font-semibold text-warn-ink">{err}</p> : null}
          {screen === "details" ? (
            <DetailsForm
              details={details}
              setDetails={setDetails}
              forPay={after === "pay"}
              busy={busy}
              onBack={() => setScreen("browse")}
              onNext={() => {
                setErr("");
                if (after === "pay") setScreen("pay");
                else void sendToCashier();
              }}
            />
          ) : screen === "pay" ? (
            <>
              <h1 className="text-2xl font-black">How would you like to pay?</h1>
              <Big title="Mobile Money" sub="Approve on your phone with your PIN" onClick={() => { setErr(""); setScreen("number"); }} />
              <Big title="Card" sub="Scan a QR and pay on your phone" disabled={busy} onClick={() => void pay({ method: "card" })} />
              <button type="button" disabled={busy} onClick={() => void sendToCashier()} className="flex min-h-[60px] flex-col justify-center gap-0.5 border border-steel-2 px-4 text-left">
                <b className="text-[15px]">Cash, or pay at the counter</b>
                <span className="text-[13px] text-white/60">Your order goes to the cashier</span>
              </button>
              <button type="button" onClick={() => setScreen("details")} className="self-start py-1.5 text-sm font-bold text-brand-light underline underline-offset-4">
                Change my details
              </button>
            </>
          ) : screen === "number" ? (
            <MomoNumber initial={customer.phone} busy={busy} onBack={() => setScreen("pay")} onSend={(phone, provider) => void pay({ method: "momo", phone, provider })} />
          ) : screen === "otp" ? (
            <Otp busy={busy} message={intent?.message ?? null} onSend={(c) => void sendOtp(c)} onBack={() => setScreen("pay")} />
          ) : screen === "qr" && intent?.authorizationUrl ? (
            <>
              <h1 className="text-2xl font-black">Scan to pay on your phone</h1>
              <div className="flex flex-wrap items-center gap-4">
                <div className="bg-white p-2.5">
                  <Qr text={intent.authorizationUrl} size={210} label="Payment QR code" />
                </div>
                <p className="min-w-[160px] flex-1 text-[15px] leading-relaxed text-white/80">Point your phone's camera here. A secure Paystack page opens with {money(total)} filled in.</p>
              </div>
              <Waiting text="Waiting for your payment. It confirms by itself." />
              <button type="button" onClick={() => setScreen("pay")} className="self-start py-1.5 text-sm font-bold text-brand-light underline underline-offset-4">
                Pay another way
              </button>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-black">Check your phone</h1>
              <p className="text-base leading-relaxed text-white/80">
                Approve {money(total)} on <b className="font-mono text-white">{intent?.phone ?? "your phone"}</b>{intent?.provider ? ` (${providerLabel(intent.provider)})` : ""} by entering your MoMo PIN.
              </p>
              <p className="text-sm text-white/55">Never tell anyone your PIN, including staff.</p>
              <Waiting text="Waiting for you to approve" />
              <button type="button" onClick={() => setScreen("number")} className="self-start py-1.5 text-sm font-bold text-brand-light underline underline-offset-4">
                Wrong number? Change it
              </button>
            </>
          )}
        </aside>
      </div>
    );

  const shown = items
    .filter((i) => category === "All" || i.category === category)
    .sort((a, b) => (a.stock === "out" ? 1 : b.stock === "out" ? -1 : 0));
  return (
    <div className="flex min-h-full flex-col bg-ink text-white lg:h-full lg:flex-row" onPointerDown={() => (touched.current = Date.now())}>
      <section className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-steel px-5 py-3">
          <Brand sub="Browse and pick" />
          <span className="text-sm text-white/60">{cat.mode === "pay" ? "Pick, pay here, collect at the counter" : "Pick, then send your order to the cashier"}</span>
        </div>
        <div className="no-scrollbar flex gap-1 overflow-x-auto border-b border-steel px-5 py-2.5">
          {["All", ...cat.categories].map((c) => (
            <button key={c} type="button" onClick={() => setCategory(c)} className={cx("h-10 flex-none px-4 text-sm font-bold", category === c ? "bg-white text-ink" : "bg-steel text-white/85 hover:text-white")}>
              {c}
            </button>
          ))}
        </div>
        <div className="grid min-h-0 flex-1 grid-cols-[repeat(auto-fill,minmax(164px,1fr))] content-start gap-2.5 overflow-y-auto p-4">
          {shown.map((p) => {
            const q = basket[p.id] ?? 0;
            const out = p.stock === "out";
            return (
              <div key={p.id} className={cx("flex flex-col bg-graphite", q ? "outline outline-2 outline-brand-light" : "")}>
                <button type="button" disabled={out} onClick={() => setQty(p.id, q + 1)} aria-label={`Add ${p.name}`} className="relative">
                  <Pic item={p} className="aspect-[4/3] w-full" />
                  {q ? <span className="absolute right-0 top-0 bg-brand-light px-1.5 py-0.5 font-mono text-xs font-semibold text-ink">{q} in order</span> : null}
                  {out ? <span className="absolute inset-x-0 bottom-0 bg-ink/80 py-1 text-center text-xs font-bold">Out of stock</span> : null}
                </button>
                <div className="flex flex-1 flex-col gap-1 px-2.5 pb-2.5 pt-2">
                  {p.brand ? <span className="truncate text-[10.5px] font-bold uppercase tracking-wider text-white/45">{p.brand}</span> : null}
                  <span className="line-clamp-2 min-h-[2.4em] text-[13.5px] font-semibold leading-tight">{p.name}</span>
                  <div className="mt-auto flex items-end justify-between gap-2 pt-1">
                    <span className="flex flex-col">
                      <span className="font-mono text-[15px] font-semibold">{money(p.price)}</span>
                      {cat.showStock && p.stock === "low" ? <span className="text-[11px] text-[#ffb36b]">Only {p.left ?? "a few"} left</span> : null}
                    </span>
                    {q ? (
                      <span className="flex items-center bg-ink">
                        <button type="button" aria-label={`One less ${p.name}`} onClick={() => setQty(p.id, q - 1)} className="grid h-9 w-9 place-items-center text-lg">−</button>
                        <span className="w-5 text-center font-mono text-sm">{q}</span>
                        <button type="button" aria-label={`One more ${p.name}`} onClick={() => setQty(p.id, q + 1)} className="grid h-9 w-9 place-items-center text-lg">+</button>
                      </span>
                    ) : (
                      <button type="button" disabled={out} onClick={() => setQty(p.id, 1)} aria-label={`Add ${p.name}`} className="grid h-10 w-10 flex-none place-items-center bg-brand disabled:bg-steel-2">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {!shown.length ? <p className="col-span-full py-10 text-center text-white/60">Nothing to show here yet.</p> : null}
        </div>
      </section>
      <aside className="flex w-full flex-col bg-graphite lg:w-[340px]">
        <div className="flex items-baseline justify-between px-4 pb-2 pt-4">
          <h2 className="text-lg font-black">My order</h2>
          <span className="font-mono text-xs text-white/55">{count} item{count === 1 ? "" : "s"}</span>
        </div>
        <div className="max-h-56 min-h-0 flex-1 overflow-y-auto px-4 lg:max-h-none">
          {!lines.length ? <p className="py-8 text-center text-sm text-white/55">Tap + on anything you'd like.</p> : null}
          {lines.map((l) => (
            <div key={l.item.id} className="flex items-center gap-2.5 border-b border-steel-2 py-2.5">
              <Pic item={l.item} className="h-11 w-11 flex-none" />
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="truncate text-[13px] font-semibold">{l.item.name}</span>
                <span className="font-mono text-[12.5px] text-white/65">{money(l.item.price * l.qty)}</span>
              </span>
              <span className="flex items-center border border-steel-2">
                <button type="button" aria-label={`One less ${l.item.name}`} onClick={() => setQty(l.item.id, l.qty - 1)} className="grid h-9 w-8 place-items-center">−</button>
                <span className="w-6 text-center font-mono text-sm">{l.qty}</span>
                <button type="button" aria-label={`One more ${l.item.name}`} onClick={() => setQty(l.item.id, l.qty + 1)} className="grid h-9 w-8 place-items-center">+</button>
              </span>
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-2 bg-ink px-4 py-4">
          {err ? <p role="alert" className="bg-warn-tint px-3 py-2 text-xs font-semibold text-warn-ink">{err}</p> : null}
          <div className="flex items-baseline justify-between">
            <span>Total</span>
            <span className="font-mono text-2xl font-semibold">{money(total)}</span>
          </div>
          {cat.mode === "pay" && hasDevice && !canPay ? <p className="text-xs text-white/55">Orders with a phone or laptop are paid at the counter.</p> : null}
          {canPay ? (
            <button type="button" disabled={!count || busy} onClick={() => { setErr(""); setAfter("pay"); setScreen("details"); }} className="h-12 bg-brand text-base font-black disabled:bg-steel-2 disabled:text-white/45">
              Pay now
            </button>
          ) : null}
          <button type="button" disabled={!count || busy} onClick={() => { setErr(""); setAfter("send"); setScreen("details"); }} className={cx("font-bold disabled:opacity-45", canPay ? "h-11 border border-steel-2 text-sm" : "h-12 bg-brand text-base font-black")}>
            {canPay ? "Pay at the counter instead" : "Send to the cashier"}
          </button>
        </div>
      </aside>
    </div>
  );
}

/** Product photo on a white plate (photos are shot on white), or the name when there's none. */
function Pic({ item, className }: { item: CatItem; className?: string }) {
  const [bad, setBad] = useState(false);
  return (
    <span className={cx("grid place-items-center overflow-hidden bg-white", className)}>
      {item.image && !bad ? (
        <img src={mediaUrl(item.image, 400)} alt="" loading="lazy" onError={() => setBad(true)} className="h-full w-full object-contain p-1.5" />
      ) : (
        <span className="px-2 text-center text-[11px] font-bold leading-tight text-[#5a636c]">{item.name.split(" ").slice(0, 3).join(" ")}</span>
      )}
    </span>
  );
}

function DetailsForm({ details, setDetails, forPay, busy, onBack, onNext }: { details: Details; setDetails: (d: Details) => void; forPay: boolean; busy: boolean; onBack: () => void; onNext: () => void }) {
  const ph = ghPhone(details.phone);
  const phone = ph.keep;
  const emailOk = !details.email.trim() || /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(details.email.trim());
  const ok = (forPay ? ph.ok : !phone || ph.ok) && emailOk;
  const input = "h-12 w-full border border-steel-2 bg-ink px-3 text-[16px] text-white outline-none placeholder:text-white/35 focus:border-brand-light";
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (ok) onNext();
      }}
    >
      <h1 className="text-2xl font-black">Your details</h1>
      <p className="text-sm text-white/65">{forPay ? "Your receipt and warranty are sent to your phone." : "So the cashier can find your order. All optional."}</p>
      <label className="flex flex-col gap-1">
        <span className="text-sm font-semibold">Phone number{forPay ? "" : " (optional)"}</span>
        <input inputMode="tel" autoComplete="tel" value={phone} onChange={(e) => setDetails({ ...details, phone: digits(e.target.value) })} placeholder="024 000 0000" className={cx(input, "font-mono text-lg")} />
        {phone && !ph.ok ? <span className="text-xs text-[#ffb36b]">{PHONE_HINT}</span> : ph.ok ? <span className="font-mono text-xs text-white/55">{fmtPhone(ph.local)}</span> : null}
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-sm font-semibold">Name (optional)</span>
        <input autoComplete="name" value={details.name} onChange={(e) => setDetails({ ...details, name: e.target.value.slice(0, 60) })} placeholder="e.g. Kwame Asante" className={input} />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-sm font-semibold">Email for the receipt (optional)</span>
        <input type="email" inputMode="email" autoComplete="email" value={details.email} onChange={(e) => setDetails({ ...details, email: e.target.value.slice(0, 200) })} placeholder="name@example.com" className={input} />
        {!emailOk ? <span className="text-xs text-[#ffb36b]">Check the email address</span> : null}
      </label>
      <button type="submit" disabled={!ok || busy} className="mt-1 h-14 bg-brand text-lg font-black disabled:bg-steel-2 disabled:text-white/45">
        {busy ? "Sending…" : forPay ? "Continue to payment" : "Send to the cashier"}
      </button>
      <button type="button" onClick={onBack} className="self-start py-1.5 text-sm font-bold text-brand-light underline underline-offset-4">
        Back to my order
      </button>
    </form>
  );
}

function Big({ title, sub, onClick, disabled }: { title: string; sub: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button type="button" disabled={disabled} onClick={onClick} className="flex min-h-[72px] flex-col justify-center gap-0.5 border-2 border-steel bg-steel px-4 text-left hover:border-brand-light disabled:opacity-50">
      <b className="text-lg">{title}</b>
      <span className="text-[13px] text-white/65">{sub}</span>
    </button>
  );
}
function Waiting({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-3 bg-ink px-4 py-3 text-[15px]">
      <span className="h-2.5 w-2.5 flex-none animate-pulse bg-warn" />
      {text}
    </div>
  );
}
function Pad({ onKey }: { onKey: (k: string) => void }) {
  return (
    <div className="grid grid-cols-3 gap-1">
      {["1", "2", "3", "4", "5", "6", "7", "8", "9", "C", "0", "⌫"].map((k) => (
        <button key={k} type="button" onClick={() => onKey(k)} aria-label={k === "⌫" ? "Delete" : k === "C" ? "Clear" : k} className="h-12 bg-steel font-mono text-xl font-semibold hover:bg-steel-2">
          {k}
        </button>
      ))}
    </div>
  );
}
function MomoNumber({ initial, busy, onBack, onSend }: { initial: string; busy: boolean; onBack: () => void; onSend: (phone: string, provider: Provider) => void }) {
  const [raw, setNum] = useState(initial);
  const [net, setNet] = useState<Provider | null>(null);
  const ph = ghPhone(raw);
  const num = ph.local;
  const shown = net ?? (ph.ok ? guessProvider(num) : null);
  return (
    <>
      <h1 className="text-2xl font-black">Mobile Money number</h1>
      <p className="text-sm text-white/65">The number that will pay. It can differ from your contact number.</p>
      <div aria-live="polite" className="flex h-14 items-center justify-between border-2 border-brand-light bg-ink px-3">
        <span className="font-mono text-2xl font-semibold tracking-wide">{raw ? (ph.ok ? fmtPhone(num) : raw) : <span className="text-white/35">0__ ___ ____</span>}</span>
        <span className="text-sm font-bold text-brand-light">{shown ? providerLabel(shown) : ""}</span>
      </div>
      {raw && !ph.ok ? <span className="text-xs text-[#ffb36b]">{PHONE_HINT}</span> : null}
      <Pad onKey={(k) => setNum((v) => (k === "C" ? "" : k === "⌫" ? v.slice(0, -1) : ghPhone(v + k).keep))} />
      {ph.ok && !guessProvider(num) ? (
        <div role="radiogroup" aria-label="Network" className="grid grid-cols-3 gap-1">
          {PROVIDERS.map((p) => (
            <button key={p.id} type="button" role="radio" aria-checked={shown === p.id} onClick={() => setNet(p.id)} className={cx("h-11 border-2 text-sm font-bold", shown === p.id ? "border-brand-light bg-white text-ink" : "border-steel bg-steel")}>
              {p.label}
            </button>
          ))}
        </div>
      ) : null}
      <button type="button" disabled={busy || !ph.ok || !shown} onClick={() => shown && onSend(num, shown)} className="h-14 bg-brand text-lg font-black disabled:bg-steel-2 disabled:text-white/45">
        {busy ? "Sending…" : "Send me the payment request"}
      </button>
      <button type="button" onClick={onBack} className="self-start py-1.5 text-sm font-bold text-brand-light underline underline-offset-4">
        Pay another way
      </button>
    </>
  );
}
function Otp({ message, busy, onSend, onBack }: { message: string | null; busy: boolean; onSend: (code: string) => void; onBack: () => void }) {
  const [code, setCode] = useState("");
  return (
    <>
      <h1 className="text-2xl font-black">Type the code you received</h1>
      <p className="text-sm text-white/70">{message || "A code was sent to your phone by SMS."} Take your time; the payment waits for it.</p>
      <div className="flex h-14 items-center border-2 border-brand-light bg-ink px-3 font-mono text-2xl font-semibold tracking-[0.3em]">{code || <span className="text-white/35">______</span>}</div>
      <Pad onKey={(k) => setCode((v) => (k === "C" ? "" : k === "⌫" ? v.slice(0, -1) : (v + k).slice(0, 8)))} />
      <button type="button" disabled={busy || code.length < 4} onClick={() => onSend(code)} className="h-14 bg-brand text-lg font-black disabled:bg-steel-2 disabled:text-white/45">
        {busy ? "Checking…" : "Confirm code"}
      </button>
      <button type="button" onClick={onBack} className="self-start py-1.5 text-sm font-bold text-brand-light underline underline-offset-4">
        Pay another way
      </button>
    </>
  );
}
